import React, { useState, useEffect } from 'react';
import { PatientFile, PatientFileType } from '../../types/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface EditFileModalProps {
    isOpen: boolean;
    onClose: () => void;
    file: PatientFile | null;
    fileTypes: PatientFileType[];
    onSave: (id: number, description: string, fileTypeId: number) => Promise<void>;
    isSaving?: boolean;
}

export const EditFileModal: React.FC<EditFileModalProps> = ({
    isOpen,
    onClose,
    file,
    fileTypes,
    onSave,
    isSaving = false,
}) => {
    const [description, setDescription] = useState('');
    const [selectedFileTypeId, setSelectedFileTypeId] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    // Update local state when file changes
    useEffect(() => {
        if (file) {
            setDescription(file.description || '');
            setSelectedFileTypeId(file.patient_file_type_id || 0);
        }
    }, [file]);

    const handleSave = async () => {
        if (!selectedFileTypeId) {
            setError('Please select a file type');
            return;
        }

        try {
            setError(null);
            await onSave(file.id, description, selectedFileTypeId);
            // onClose will be called by parent after successful save
        } catch (err: any) {
            setError(err.message || 'Failed to save file');
        }
    };

    const handleCancel = () => {
        setDescription(file?.description || '');
        setSelectedFileTypeId(file?.patient_file_type_id || 0);
        setError(null);
        onClose();
    };

    if (!isOpen || !file) return null;

    return (
        <ModalWrapper modalId="EditFileModal"
            isOpen={isOpen}
            onClose={handleCancel}
            title="Edit file"
            isLoading={isSaving}
        >
            {/* Description Input */}
            <div className="mb-4">
                <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={POPUP_STYLES.input}
                    placeholder="Enter file description..."
                    disabled={isSaving}
                />
            </div>

            {/* File Type Dropdown */}
            <div className="mb-6">
                <div className="relative">
                    <select
                        value={selectedFileTypeId}
                        onChange={(e) => setSelectedFileTypeId(Number(e.target.value))}
                        className="w-full px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent appearance-none text-[#366A75] pr-10"
                        disabled={isSaving}
                    >
                        <option value={0}>Select file type...</option>
                        {fileTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.description}
                            </option>
                        ))}
                    </select>
                    {/* Clear button */}
                    {selectedFileTypeId !== 0 && (
                        <button
                            onClick={() => setSelectedFileTypeId(0)}
                            title="Clear file type"
                            aria-label="Clear file type"
                            className="absolute right-8 top-1/2 -translate-y-1/2 text-[#366A75] hover:text-[#2D5A60]"
                            disabled={isSaving}
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </button>
                    )}
                    {/* Dropdown arrow */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-4">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
